import React, { useCallback, useEffect, useState } from 'react'
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd'
import { loader } from 'react-global-loader'
import { useNavigate } from 'react-router-dom'

import Breadcrumb from '@components/Breadcrumb'
import Button from '@components/Button'
import Divider from '@components/Divider'
import EmptyRow from '@components/EmptyRow'
import Flex from '@components/Flex'
import PageTitle from '@components/PageTitle'
import SearchInput from '@components/SearchInput'
import Spinner from '@components/Spinner'
import Table from '@components/Table'
import Td from '@components/Td'
import Template from '@components/Template'
import Tr from '@components/Tr'
import { successMessages } from '@constants/feedbackMessages'
import {
	MenuAlt2Icon,
	PencilAltIcon,
	TrashIcon,
} from '@heroicons/react/outline'
import { categoriesApi } from '@services/Categories'
import { loadingModal, openAlertModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'
import { useFilters, useInputBounce, useQuery } from '@utils/hooks'

const styles = {
	searchContainer: 'gap-[8px] mb-[32px] items-end flex-wrap',
	tag: 'font-medium text-blue-600 underline underline-offset-2',
	divider: 'mt-[32px] mb-[12px]',
	avatar: 'rounded-full max-w-[40px] max-h-[40px] min-w-[40px] min-h-[40px]',
}

function Categories() {
	const pages = [{ name: 'Categorias', href: '#', current: true }]
	const dispatch = useAppDispatch()
	const query = useQuery()
	const navigate = useNavigate()

	const searchQs = query.get('search') || ''

	const [search, setSearch] = useState(decodeURIComponent(searchQs))
	const [searchQuery, setSearchQuery] = useState(decodeURIComponent(searchQs))

	const { data, isLoading } = categoriesApi.useListQuery({
		search: encodeURIComponent(searchQuery),
	})

	const [
		deleteCategories,
		{ isLoading: loadingDelete, isSuccess: deleteSuccess },
	] = categoriesApi.useDeleteMutation()

	const [reorderCategories] = categoriesApi.useReorderMutation()

	const [categories, setCategories] = useState(data?.data)

	const searchCallback = useCallback(
		(value: string) => {
			navigate('/categories')
			setSearchQuery(value)
		},
		[navigate]
	)

	useEffect(() => {
		dispatch(loadingModal({ loading: loadingDelete }))

		if (deleteSuccess) {
			dispatch(notificationCreators.show(successMessages.deleteCategories))
		}
	}, [dispatch, loadingDelete, deleteSuccess])

	useEffect(() => {
		setCategories(data?.data)
	}, [data])

	useInputBounce(search, searchCallback)

	useFilters({
		search: searchQuery,
		fallbackUrl: '/categories',
	})

	async function handleDragEnd(result: DropResult) {
		if (!categories || !result.destination) {
			return
		}

		const from = result.source.index
		const to = result.destination.index

		if (from === to) {
			return
		}

		// A posição de destino vem do item que hoje ocupa aquela linha, não do índice
		// da página: assim o arrasto continua correto a partir da segunda página.
		const newPosition = categories[to].position

		loader.show()

		try {
			await reorderCategories({
				categoryId: categories[from].id,
				newPosition,
			}).unwrap()

			const list = [...categories]
			const [removed] = list.splice(from, 1)

			list.splice(to, 0, removed)

			setCategories(list)
		} catch {
			// O refetch da lista devolve a ordem que o servidor gravou.
		}

		loader.hide()
	}

	return (
		<>
			<Template>
				<Breadcrumb pages={pages} />
				<PageTitle title='Categorias' />

				<Flex className={styles.searchContainer}>
					<SearchInput
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className='flex-1'
						placeholder='Buscar Categoria'
					/>

					<Button onClick={() => navigate(`/categories/cadastrar-categoria`)}>
						Adicionar Categoria
					</Button>
				</Flex>

				{isLoading ? (
					<div className='flex justify-center'>
						<Spinner size='md' />
					</div>
				) : (
					<DragDropContext onDragEnd={handleDragEnd}>
						<Table
							css='overflow-x-visible'
							th={[
								{ label: 'id' },
								{ label: 'nome' },
								{ label: 'ações' },
								{ label: 'ordem' },
							]}
							droppable
							variant='mixed'
						>
							{categories && categories.length > 0 ? (
								categories.map(({ id, name }, index) => (
									<Draggable key={id} draggableId={String(id)} index={index}>
										{(provided) => (
											<Tr
												ref={provided.innerRef}
												{...provided.draggableProps}
												{...provided.dragHandleProps}
											>
												<Td>{id}</Td>
												<Td className='w-[75%] px-6'>
													<Flex className='items-center gap-4'>{name}</Flex>
												</Td>

												<Td>
													<Flex className='items-center gap-4'>
														<Button
															className='!px-2 !py-2'
															leftIcon={<PencilAltIcon />}
															disabled={false}
															variant='outline'
															onClick={() =>
																navigate(`/categories/editar-categoria/${id}`)
															}
														>
															<></>
														</Button>

														<Button
															className='!px-2 !py-2'
															leftIcon={<TrashIcon />}
															disabled={false}
															variant='outline'
															onClick={() => {
																const removeCategories = {
																	title: 'Remover Categoria',
																	message:
																		'Tem certeza que deseja remover a categoria?',
																	buttonLabel: 'Remover Categoria',
																	onClick: () =>
																		deleteCategories({
																			id,
																		}),
																}

																dispatch(openAlertModal(removeCategories))
															}}
														>
															<></>
														</Button>
													</Flex>
												</Td>

												<Td>
													<MenuAlt2Icon className='w-6 h-6' />
												</Td>
											</Tr>
										)}
									</Draggable>
								))
							) : (
								<EmptyRow tdAmount={4} message='Nenhuma categoria encontrada' />
							)}
						</Table>
					</DragDropContext>
				)}

				<Divider className={styles.divider} />
			</Template>
		</>
	)
}

export default Categories
